/**
 * Pipeline Utilities — shared helpers extracted from QueryRouter.
 *
 * These functions are used by multiple pipeline operations. Extracting them
 * into a single module avoids duplication and ensures consistent behavior
 * across ops that share SQL fixers, camera logic, highlighting, etc.
 *
 * Phase 2 extraction from:
 *   - QueryRouter._flyToTarget (lines 554-591)
 *   - QueryRouter._flyToBbox (lines 1865-1903)
 *   - QueryRouter._isGlobalTarget (lines 596-599)
 *   - QueryRouter._fixSQLUrls (lines 391-397)
 *   - QueryRouter._fixSubregionSQL (lines 405-420)
 *   - QueryRouter._fixContinentSQL (lines 427-449)
 *   - QueryRouter._fixCityCountrySQL (lines 455-471)
 *   - QueryRouter._applySubregionFilter (lines 478-494)
 *   - QueryRouter._interpolateTemplate (lines 499-512)
 *   - QueryRouter._highlightAnalyticsTarget (lines 605-646)
 *   - QueryRouter._highlightContinent (lines 652-680)
 *   - QueryRouter._overlayCityMarkers (lines 685-714)
 *   - QueryRouter._overlayBoundariesForCities (lines 800-827)
 *   - QueryRouter._resolveClassificationBreaks (lines 1824-1863)
 *   - Module-level: _getBbox (lines 2405-2419)
 *   - Module-level: _buildCategoryFilter (lines 2457-2481)
 *   - Module-level: _getCategoryParquetFiles (lines 2428-2455)
 *   - Module-level: _formatPOICategory (lines 2486-2514)
 *   - Module-level: _getOvertureLegend (lines 2352-2399)
 */

import { findLocation, CONTINENT_VIEWS, SUBREGION_COUNTRIES } from '../utils/geo-utils.js'
import { duckdbEngine } from '../data/duckdb-engine.js'
import { getR2Url } from '../data/query-builder.js'
import { prepareBoundaryStyle, computeBbox } from '../globe/boundary-layer.js'
import { mainlandClusterBounds } from '../layers/boundary-mask.js'
import { loadDataset } from '../data/loader.js'
import { iso3ToIso2 } from '../utils/iso-codes.js'
import { getCapitalsForCountry } from '../data/state-capitals.js'
import catalog from '../data/catalog.js'

// ══════════════════════════════════════════════════════════════════════
// Camera utilities
// ══════════════════════════════════════════════════════════════════════

/**
 * Check if a location string refers to the whole world/globe.
 * @param {string} location
 * @returns {boolean}
 */
export function isGlobalTarget(location) {
  if (!location) return false
  const global = ['world', 'global', 'earth', 'all countries', 'all', 'worldwide', 'the world']
  return global.includes(location.toLowerCase().trim())
}

/**
 * Fly to a target, using local index first, then LLM coordinates as fallback.
 * If target is "world" / "global" / null, zoom out to global view instead.
 *
 * @param {object} target — { location, coordinates }
 * @param {object|null} resultGeojson — multi-feature result GeoJSON (for bbox fitting)
 * @param {object} services — ctx.services (needs animator)
 * @returns {object|null} Location result from the index, or null
 */
export function flyToTarget(target, resultGeojson, services) {
  const { animator } = services

  // Check for world/global queries — don't fly to a specific location
  if (!target.location || isGlobalTarget(target.location)) {
    // Zoom out to global view if currently zoomed in
    const currentZoom = animator.globe.viewState?.zoom ?? 1.5
    if (currentZoom > 2.5) {
      animator.globe.flyTo(0, 20, 1.5)
    }
    return null
  }

  // If we have multi-feature results, fit to their combined bounding box
  // But prefer curated continent/region views over raw bbox (avoids Alaska/Greenland skew)
  if (resultGeojson?.features?.length > 1) {
    const curatedView = CONTINENT_VIEWS[target.location]
    if (curatedView) {
      animator.globe.flyTo(curatedView.longitude, curatedView.latitude, curatedView.zoom)
      return { centroid: [curatedView.longitude, curatedView.latitude] }
    }
    const bbox = computeBbox(resultGeojson)
    // Sanity check — skip degenerate or world-spanning boxes
    if (bbox[0] < bbox[2] && bbox[1] < bbox[3] && bbox[2] - bbox[0] < 360) {
      animator.globe.fitToBounds(bbox)
      const center = [(bbox[0] + bbox[2]) / 2, (bbox[1] + bbox[3]) / 2]
      return { centroid: center }
    }
  }

  if (target.location) {
    const loc = animator.flyToLocation(target.location)
    if (loc) return loc
  }
  if (target.coordinates && Array.isArray(target.coordinates) && target.coordinates.length === 2) {
    animator.flyToCoordinates(target.coordinates[0], target.coordinates[1])
    return { centroid: target.coordinates, zoom: 8 }
  }
  return null
}

/**
 * Fly camera to fit a GeoJSON bounding box with smart zoom.
 * Uses median feature centers for countries with distant territories.
 *
 * @param {object} geojson — GeoJSON FeatureCollection
 * @param {number} maxZoom — maximum zoom level (default 6)
 * @param {object} services — ctx.services (needs animator)
 */
/**
 * Pad a degenerate bbox so it always has area.
 *
 * Reference lines (the equator, a tropic) are perfectly straight, so their
 * bbox has zero height — fitBounds would treat that as "zoom in as far as you
 * can". Give every dimension a minimum span before framing it.
 *
 * @param {number[]} bbox  [minLng, minLat, maxLng, maxLat]
 * @param {number} minSpan minimum degrees per dimension
 */
export function padBbox(bbox, minSpan = 4) {
  const grow = (lo, hi) => {
    if (hi - lo >= minSpan) return [lo, hi]
    const mid = (lo + hi) / 2
    return [mid - minSpan / 2, mid + minSpan / 2]
  }
  const [w, e] = grow(bbox[0], bbox[2])
  const [s, n] = grow(bbox[1], bbox[3])
  return [Math.max(-180, w), Math.max(-85, s), Math.min(180, e), Math.min(85, n)]
}

/**
 * Zoom levels to back off after fitting a bbox (CL-261): 1 for extents up to
 * 10°, tapering linearly to 0.5 at 25° and beyond.
 * @param {number[]} bbox [w, s, e, n]
 */
export function framingZoomOut(bbox) {
  const span = Math.max(bbox[2] - bbox[0], bbox[3] - bbox[1])
  return Math.min(1, Math.max(0.5, 1 - ((span - 10) * 0.5) / 15))
}

export function flyToBbox(geojson, maxZoom = 6, services, options = {}) {
  let bbox = computeBbox(geojson)
  if (!isFinite(bbox[0])) return

  // CL-261: frame the MAINLAND cluster, not the raw bbox. Remote islands
  // (Japan's Minamitorishima, France's Guiana, Chile's Easter Island) inflate
  // the raw extent so far that the country itself became a speck on the globe.
  // The polygons still render in full; only the camera ignores the outliers.
  if (options.mainland !== false && bbox[2] - bbox[0] < 350) {
    // 3° gap (not the DEM path's 10°): the cluster test compares against the
    // growing union bbox, so a wide gap lets a diagonal country absorb far
    // islands (Hokkaido's box pulled in Minamitorishima, 8° east of it).
    const cluster = mainlandClusterBounds(geojson, 3)
    if (cluster && cluster[0] < cluster[2] && cluster[1] < cluster[3]) bbox = cluster
  }

  // Preferred path: MapLibre's own fitBounds. It accounts for the real
  // viewport aspect ratio, edge padding and the active projection, so the
  // whole feature set actually lands on screen. The hand-rolled zoom ladder
  // below keys off the larger span only, which consistently over-zoomed
  // wide-but-short extents — "the states of Germany" landed well inside the
  // country instead of framing all sixteen. Antimeridian-spanning extents
  // keep the legacy path, which re-centres them on 180 rather than on the
  // wrong side of the planet.
  const globe = services?.animator?.globe
  if (typeof globe?.fitToBounds === 'function' && bbox[2] - bbox[0] < 350) {
    globe.fitToBounds(padBbox(bbox), {
      maxZoom,
      padding: 80,
      // Back off two zoom levels so the framed extent keeps its
      // surroundings — a country should read as a shape within its region,
      // not as a form filling the viewport edge to edge. Verified against the
      // scale bar: one level left Germany at 50 km, two lands it at 100 km,
      // which is the framing that shows every state plus its neighbours.
      // CL-261: one level (not CL-260's two) — on the globe projection the
      // fitted extent already renders smaller than Mercator math predicts, so
      // two levels left Germany small and Japan a speck. Tapers to 0.5 for
      // extents of 25°+ (they carry their own context).
      zoomOut: options.zoomOut ?? framingZoomOut(bbox),
    })
    return
  }

  const span = Math.max(bbox[2] - bbox[0], bbox[3] - bbox[1])
  let cLng, cLat

  // Antimeridian-wrapping feature (e.g. the Pacific Ocean): when a feature's
  // bbox spans nearly the whole globe in longitude, its halves sit near -180 and
  // +180, so the naive midpoint lands at lng 0 — the wrong side of the planet.
  // Centre on the antimeridian instead.
  if (bbox[2] - bbox[0] >= 350) {
    cLng = 180
    cLat = (bbox[1] + bbox[3]) / 2
  } else if (span > 60 && geojson.features?.length > 1) {
    // For very large spans (countries with distant territories), use median of feature
    // centers to avoid centering in the ocean
    const centers = geojson.features
      .map((f) => {
        const fb = getBbox(f)
        return [fb, (fb[0] + fb[2]) / 2, (fb[1] + fb[3]) / 2]
      })
      .filter(([, lng]) => isFinite(lng))

    if (centers.length > 0) {
      const lngs = centers.map((c) => c[1]).sort((a, b) => a - b)
      const lats = centers.map((c) => c[2]).sort((a, b) => a - b)
      cLng = lngs[Math.floor(lngs.length / 2)]
      cLat = lats[Math.floor(lats.length / 2)]
    } else {
      cLng = (bbox[0] + bbox[2]) / 2
      cLat = (bbox[1] + bbox[3]) / 2
    }
  } else {
    cLng = (bbox[0] + bbox[2]) / 2
    cLat = (bbox[1] + bbox[3]) / 2
  }

  // More granular zoom levels for small territories (Bug B fix)
  const computed =
    span > 100
      ? 2
      : span > 60
        ? 2.5
        : span > 40
          ? 3
          : span > 20
            ? 3.5
            : span > 10
              ? 4
              : span > 5
                ? 5
                : span > 2
                  ? 6
                  : span > 1
                    ? 7
                    : span > 0.5
                      ? 8
                      : 9
  // Clamp between minimum floor (3) and maxZoom to prevent over-zooming in both directions
  const zoom = Math.max(Math.min(computed, maxZoom), 3)
  services.animator.globe.flyTo(cLng, cLat, zoom)
}

// ══════════════════════════════════════════════════════════════════════
// SQL fixers
// ══════════════════════════════════════════════════════════════════════

/**
 * Fix bare /data/ paths in SQL to use full R2 URL.
 * @param {string} sql
 * @returns {string}
 */
export function fixSQLUrls(sql) {
  const r2Url = getR2Url()
  if (!sql) return sql

  // Tables preloaded into DuckDB as local tables (from duckdb-engine.js _preloadAggregations).
  // Rewrite HTTP URLs referencing these parquet files to use the local table name instead.
  // This is faster (no HTTP range requests) and immune to R2 CDN cache staleness.
  const preloadedTables = [
    'country_summary',
    'continent_summary',
    'temporal_population',
    'country_indicators_latest',
  ]
  for (const table of preloadedTables) {
    // Match: '...r2.dev/data/table.parquet' (any R2 base URL prefix)
    const httpPattern = new RegExp(`'https?://[^']+/data/${table}\\.parquet'`, 'g')
    if (httpPattern.test(sql)) {
      console.log(`[pipeline-utils] Rewriting HTTP URL → local table: ${table}`)
      sql = sql.replace(httpPattern, table)
    }
  }

  if (r2Url) {
    // Replace bare '/data/xxx.parquet' references with full R2 URL
    // Match single-quoted paths like '/data/places.parquet' that lack the R2 base URL
    sql = sql.replace(/'\/data\//g, `'${r2Url}/data/`)
  }
  return sql
}

/**
 * Fix subregion filters in SQL (BUG-018/019/020).
 * The LLM uses subregion = 'Middle East' or continent = 'North America' which
 * may not match the dataset's classification. Rewrite to explicit ISO code lists
 * using the authoritative SUBREGION_COUNTRIES dictionary from geo-utils.js.
 * @param {string} sql
 * @returns {string}
 */
export function fixSubregionSQL(sql) {
  if (!sql) return sql
  for (const [region, isoSet] of Object.entries(SUBREGION_COUNTRIES)) {
    const isoList = [...isoSet].map((c) => `'${c}'`).join(',')
    // Match: subregion = 'Region' or subregion ILIKE 'Region' or continent = 'Region'
    const pattern = new RegExp(
      `(?:subregion|continent|region)\\s*(?:=|ILIKE)\\s*'${region.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}'`,
      'gi'
    )
    if (pattern.test(sql)) {
      console.log(`[pipeline-utils] Fixed subregion SQL: ${region} → iso_a3 IN (${isoList})`)
      sql = sql.replace(pattern, `iso_a3 IN (${isoList})`)
    }
  }
  return sql
}

/**
 * Fix continent-as-ISO-code errors in AI-generated SQL (BUG-012).
 * The LLM sometimes writes WHERE iso_a2 = 'EU' instead of WHERE continent = 'Europe'.
 * 'EU' is not a valid country code; 'AS' is American Samoa, not Asia.
 * @param {string} sql
 * @returns {string}
 */
export function fixContinentSQL(sql) {
  if (!sql) return sql
  const continentCodeMap = {
    EU: 'Europe',
    AS: 'Asia',
    AF: 'Africa',
    SA: 'South America',
    NA: 'North America',
    OC: 'Oceania',
    AN: 'Antarctica',
  }
  return sql.replace(/iso_?a?2\s*=\s*'(EU|AS|AF|SA|NA|OC|AN)'/gi, (match, code) => {
    const continent = continentCodeMap[code.toUpperCase()]
    if (continent) {
      console.log(`[pipeline-utils] Fixed continent SQL: ${match} → continent = '${continent}'`)
      return `continent = '${continent}'`
    }
    return match
  })
}

/**
 * Fix known city→country code errors in AI-generated SQL (BUG-013).
 * The LLM sometimes maps Tokyo to 'TK' (Tokelau) instead of 'JP'.
 * @param {string} sql
 * @param {object} intent — original intent (for location context)
 * @returns {string}
 */
export function fixCityCountrySQL(sql, intent) {
  if (!sql) return sql
  const cityFixes = {
    TK: { correct: 'JP', cities: ['tokyo', 'osaka', 'kyoto'] },
  }
  for (const [wrongCode, fix] of Object.entries(cityFixes)) {
    const pattern = new RegExp(`country\\s*=\\s*'${wrongCode}'`, 'gi')
    if (pattern.test(sql)) {
      const queryText = (intent?.originalText || intent?.target?.location || '').toLowerCase()
      if (fix.cities.some((c) => queryText.includes(c))) {
        console.log(`[pipeline-utils] Fixed city country code: ${wrongCode} → ${fix.correct}`)
        sql = sql.replace(pattern, `country = '${fix.correct}'`)
      }
    }
  }
  return sql
}

/**
 * Fix countries table iso3 column references (RC-BIV-5).
 * The LLM writes `c.iso3 IN (...)` but the countries.parquet uses `iso_a3`.
 * The indicators table uses `iso3`. When tables are JOINed, the AI sometimes
 * puts the ISO filter on the wrong alias. Rewrite to use the correct column.
 * @param {string} sql
 * @returns {string}
 */
export function fixCountriesIso3(sql) {
  if (!sql) return sql
  // Only apply when SQL involves the countries table with alias 'c'
  if (!sql.includes('countries.parquet')) return sql
  // Replace c.iso3 → c.iso_a3 (countries table uses iso_a3, not iso3)
  const fixed = sql.replace(/\bc\.iso3\b/g, 'c.iso_a3')
  if (fixed !== sql) {
    console.log('[pipeline-utils] Fixed countries ISO3 column: c.iso3 → c.iso_a3')
  }
  return fixed
}

/**
 * Apply all SQL fixers in sequence.
 * Convenience wrapper for ops that need to clean LLM-generated SQL.
 * @param {string} sql
 * @param {object} intent — original intent (for city context)
 * @returns {string}
 */
export function fixSQL(sql, intent) {
  if (!sql) return sql
  let fixed = fixSQLUrls(sql)
  fixed = fixSubregionSQL(fixed)
  fixed = fixContinentSQL(fixed)
  fixed = fixCityCountrySQL(fixed, intent)
  fixed = fixCountriesIso3(fixed)
  return fixed
}

// ══════════════════════════════════════════════════════════════════════
// Data filtering
// ══════════════════════════════════════════════════════════════════════

/**
 * Post-query safeguard: filter out countries that don't belong in named subregions.
 * Uses the authoritative SUBREGION_COUNTRIES dictionary from geo-utils.js.
 * Even if the SQL or AI includes extra countries, this removes them.
 *
 * @param {object} intent — parsed intent
 * @param {object} geojson — GeoJSON FeatureCollection
 * @returns {object} Filtered GeoJSON FeatureCollection
 */
export function applySubregionFilter(intent, geojson) {
  const location = (intent.target?.location || '').toLowerCase()
  // Build lowercase→Set lookup from SUBREGION_COUNTRIES
  const allowed = Object.entries(SUBREGION_COUNTRIES).find(
    ([name]) => name.toLowerCase() === location
  )?.[1]
  if (allowed && geojson?.features?.length > 0) {
    const filtered = geojson.features.filter((f) => {
      const iso = (f.properties?.iso_a3 || f.properties?.ISO_A3 || '').toUpperCase()
      return !iso || allowed.has(iso)
    })
    if (filtered.length !== geojson.features.length) {
      console.log(
        `[pipeline-utils] Region filter: removed ${geojson.features.length - filtered.length} features from "${location}" results`
      )
    }
    return { type: 'FeatureCollection', features: filtered }
  }
  return geojson
}

// ══════════════════════════════════════════════════════════════════════
// Voice template interpolation
// ══════════════════════════════════════════════════════════════════════

/**
 * Replace template placeholders like {count}, {pop_est}, etc. with actual values.
 *
 * @param {string} template — voice response template string
 * @param {object} ctx — pipeline context (reads ctx.resultCount, ctx.resultRow)
 * @returns {string}
 */
export function interpolateTemplate(template, ctx) {
  if (!template) return template
  let result = template.replace(/\{count\}/g, ctx.resultCount ?? '?')
  // Replace any remaining {key} placeholders from last query result row
  if (ctx.resultRow) {
    result = result.replace(/\{(\w+)\}/g, (match, key) => {
      const val =
        ctx.resultRow[key] ?? ctx.resultRow[key.toLowerCase()] ?? ctx.resultRow[key.toUpperCase()]
      if (val != null) {
        return typeof val === 'number' ? val.toLocaleString() : String(val)
      }
      return match
    })
  }
  return result
}

// ══════════════════════════════════════════════════════════════════════
// Highlighting
// ══════════════════════════════════════════════════════════════════════

/**
 * Highlight the queried territory on the globe for analytical/aggregation queries.
 * Tries the location index first (countries), then admin boundaries (states/provinces).
 *
 * @param {object} intent — parsed intent
 * @param {object} ctx — pipeline context
 */
export async function highlightAnalyticsTarget(intent, ctx) {
  const { layers, adminBoundaries } = ctx.services

  // Continent-level highlight: when results come from continent_summary,
  // highlight all countries belonging to the top continent
  if (intent.target?.dataset === 'continent_summary' && ctx.resultRow?.continent) {
    await highlightContinent(ctx.resultRow.continent, ctx)
    return
  }

  const location = intent.target?.location
  if (!location || isGlobalTarget(location)) return

  // Try country-level highlight via location index
  const loc = findLocation(ctx.services.locationIndex, location)
  if (loc?.feature) {
    const geojson = { type: 'FeatureCollection', features: [loc.feature] }
    layers.highlightFeature(geojson)
    return
  }

  // Try sub-national highlight via admin boundaries (e.g., "Texas", "Bavaria")
  const abs = adminBoundaries
  if (!abs?.isReady || !intent.target?.country) return

  const countryInfo = abs.getCountry(intent.target.country)
  const iso3 = countryInfo?.iso3 || intent.target.country.toUpperCase()
  const admLevel = abs.getDefaultLevel(iso3) || 'ADM1'

  try {
    const geojson = await abs.getBoundaries(iso3, admLevel)
    if (!geojson?.features?.length) return

    const match = abs.findDivision(geojson, location)
    if (match) {
      const aStyle = prepareBoundaryStyle(
        { type: 'FeatureCollection', features: [match] },
        'highlight'
      )
      ctx.services.animator.globe.showBoundaries(aStyle.geojson, aStyle)
    }
  } catch (err) {
    console.warn('[pipeline-utils] Analytics highlight failed:', err.message)
  }
}

/**
 * Highlight all countries in a continent on the globe.
 * Used for continent-level analytical queries (e.g., "which continent has the most people?").
 *
 * @param {string} continentName
 * @param {object} ctx — pipeline context
 */
export async function highlightContinent(continentName, ctx) {
  try {
    const data = await loadDataset('countries', 'low')
    if (!data?.features) return

    const matching = data.features.filter((f) => {
      const c = f.properties.CONTINENT || f.properties.continent
      return c && c.toLowerCase() === continentName.toLowerCase()
    })

    if (matching.length === 0) return

    const geojson = { type: 'FeatureCollection', features: matching }

    // Fly to continent center
    const continentView = CONTINENT_VIEWS[continentName]
    if (continentView) {
      ctx.services.animator.globe.flyTo(
        continentView.longitude,
        continentView.latitude,
        continentView.zoom
      )
    }

    // Render as a filled highlight
    const style = prepareBoundaryStyle(geojson, 'highlight')
    ctx.services.animator.globe.showBoundaries(style.geojson, style)

    console.log(`[pipeline-utils] Highlighted ${matching.length} countries in ${continentName}`)
  } catch (err) {
    console.warn('[pipeline-utils] Continent highlight failed:', err.message)
  }
}

// ══════════════════════════════════════════════════════════════════════
// City overlays
// ══════════════════════════════════════════════════════════════════════

/**
 * Overlay capital city markers for a country (used on top of admin boundaries).
 *
 * @param {string} iso3 — 3-letter ISO country code
 * @param {object} ctx — pipeline context
 */
export async function overlayCityMarkers(iso3, ctx) {
  const { layers } = ctx.services

  // Check for curated capital data first (accurate city-proper populations)
  const curated = getCapitalsForCountry(iso3)
  if (curated.replaceAll && curated.features.length > 0) {
    const geojson = { type: 'FeatureCollection', features: curated.features }
    console.log(
      `[pipeline-utils] Overlaying ${curated.features.length} curated capitals for ${iso3}`
    )
    layers.showProportionalSymbols(geojson, 'pop_max', {})
    return
  }

  // Fallback: query Natural Earth capitals from DuckDB
  if (!duckdbEngine.isReady) return

  const iso2 = iso3ToIso2(iso3)
  if (!iso2) return

  const r2Url = getR2Url()
  const citiesTable = r2Url ? `'${r2Url}/data/cities.parquet'` : `'/data/cities.parquet'`
  const sql = `SELECT name, adm0name, adm1name, pop_max, featurecla, ST_AsGeoJSON(geometry) as geojson FROM ${citiesTable} WHERE iso_a2 = '${iso2}' AND featurecla ILIKE '%capital%' ORDER BY pop_max DESC`

  try {
    const cityGeoJSON = await duckdbEngine.spatialQuery(sql)
    if (cityGeoJSON?.features?.length) {
      console.log(
        `[pipeline-utils] Overlaying ${cityGeoJSON.features.length} capital cities for ${iso3}`
      )
      layers.showProportionalSymbols(cityGeoJSON, 'pop_max', {})
    }
  } catch (err) {
    console.warn('[pipeline-utils] City marker overlay failed:', err.message)
  }
}

/**
 * Overlay admin boundaries when showing city markers for a specific country.
 * Renders yellow outlines of states/provinces behind the teardrop markers.
 *
 * @param {object} intent — parsed intent
 * @param {object} ctx — pipeline context
 */
export async function overlayBoundariesForCities(intent, ctx) {
  const location = intent.target?.location
  if (!location || isGlobalTarget(location)) return

  const abs = ctx.services.adminBoundaries
  if (!abs?.isReady) return

  // Resolve country from target location
  const countryInfo = abs.getCountry(location)
  if (!countryInfo) {
    // Try as continent — don't overlay boundaries for continent-level queries
    if (CONTINENT_VIEWS[location]) return
    return
  }

  const iso3 = countryInfo.iso3
  const admLevel = abs.getDefaultLevel(iso3) || 'ADM1'

  try {
    const geojson = await abs.getBoundaries(iso3, admLevel)
    if (!geojson?.features?.length) return

    const style = prepareBoundaryStyle(geojson, 'outline')
    ctx.services.animator.globe.showBoundaries(style.geojson, style)
  } catch (err) {
    console.warn('[pipeline-utils] Boundary overlay for cities failed:', err.message)
  }
}

// ══════════════════════════════════════════════════════════════════════
// Classification breaks
// ══════════════════════════════════════════════════════════════════════

/**
 * Resolve classification breaks for a choropleth.
 * Tries: (1) pre-computed catalog breaks, (2) DuckDB quantile query, (3) null for local computation.
 *
 * @param {string} iso3
 * @param {string} admLevel
 * @param {string} attribute
 * @param {string} metric
 * @param {object|null} catalogBreaks — Pre-computed breaks from catalog, if any
 * @param {boolean} hasGeoparquet — Whether GeoParquet is available for DuckDB
 * @returns {Promise<number[]|null>} Array of 6 threshold values, or null
 */
export async function resolveClassificationBreaks(
  iso3,
  admLevel,
  attribute,
  metric,
  catalogBreaks,
  hasGeoparquet
) {
  // Path 1: Catalog has pre-computed breaks — use immediately
  if (catalogBreaks?.breaks?.length > 0) {
    console.log(
      `[pipeline-utils] Using catalog breaks for ${iso3}/${admLevel}/${attribute}: [${catalogBreaks.breaks.join(', ')}]`
    )
    return catalogBreaks.breaks
  }

  // Path 2: GeoParquet available + DuckDB ready — compute quantile breaks via SQL
  if (hasGeoparquet && duckdbEngine.isReady) {
    try {
      const parquetUrl = catalog.getGeoParquetUrl(iso3, admLevel)
      if (!parquetUrl) return null

      // Build PERCENTILE_CONT query for 7-class quantile breaks (6 thresholds)
      const percentiles = [0.14, 0.28, 0.43, 0.57, 0.71, 0.86]
      const selectParts = percentiles.map(
        (p, i) => `PERCENTILE_CONT(${p}) WITHIN GROUP (ORDER BY ${attribute}) AS p${i}`
      )
      const sql = `SELECT ${selectParts.join(', ')} FROM '${parquetUrl}' WHERE ${attribute} IS NOT NULL AND ${attribute} > 0`

      console.log(`[pipeline-utils] DuckDB breaks query: ${sql}`)
      const t0 = performance.now()
      const rows = await duckdbEngine.query(sql)
      const elapsed = performance.now() - t0

      if (rows.length > 0) {
        const breaks = percentiles
          .map((_, i) => rows[0][`p${i}`])
          .filter((v) => v != null && !isNaN(v))
        if (breaks.length >= 4) {
          console.log(
            `[pipeline-utils] DuckDB breaks computed in ${elapsed.toFixed(0)}ms: [${breaks.map((b) => b.toFixed(1)).join(', ')}]`
          )
          return breaks
        }
      }
    } catch (err) {
      console.warn(`[pipeline-utils] DuckDB breaks query failed: ${err.message}`)
    }
  }

  // Path 3: No pre-computed breaks available — return null (local computation)
  return null
}

// ══════════════════════════════════════════════════════════════════════
// GeoJSON utilities
// ══════════════════════════════════════════════════════════════════════

/**
 * Compute bounding box of a GeoJSON feature without Turf.js.
 * @param {object} feature — GeoJSON Feature
 * @returns {number[]} [minLng, minLat, maxLng, maxLat]
 */
export function getBbox(feature) {
  let minLng = Infinity
  let minLat = Infinity
  let maxLng = -Infinity
  let maxLat = -Infinity
  function walk(coords) {
    if (typeof coords[0] === 'number') {
      if (coords[0] < minLng) minLng = coords[0]
      if (coords[1] < minLat) minLat = coords[1]
      if (coords[0] > maxLng) maxLng = coords[0]
      if (coords[1] > maxLat) maxLat = coords[1]
    } else {
      coords.forEach(walk)
    }
  }
  walk(feature.geometry.coordinates)
  return [minLng, minLat, maxLng, maxLat]
}

export function featureCentroid(feature) {
  if (!feature?.geometry?.coordinates) return null
  const [minLng, minLat, maxLng, maxLat] = getBbox(feature)
  if (!isFinite(minLng) || !isFinite(minLat)) return null
  return [(minLng + maxLng) / 2, (minLat + maxLat) / 2]
}

// ══════════════════════════════════════════════════════════════════════
// POI utilities
// ══════════════════════════════════════════════════════════════════════

/**
 * Build a SQL category filter clause for POI counting.
 * @param {string} category
 * @returns {string} SQL WHERE clause fragment
 */
export function buildCategoryFilter(category) {
  // Coerce non-string input (numbers, booleans) so a misrouted numeric
  // filter.value — e.g. from "more than 100 million people" — can't crash
  // the router via category.replace is not a function. Related: CL-035.
  if (typeof category !== 'string') {
    if (category == null) return '1=0'
    category = String(category)
  }
  const categoryMap = {
    hospital: "category IN ('hospital', 'medical_center')",
    school:
      "category IN ('school', 'elementary_school', 'middle_school', 'high_school', 'private_school', 'public_school')",
    university: "category IN ('college_university', 'university')",
    airport: "category = 'airport'",
    museum:
      "category IN ('museum', 'art_gallery', 'art_museum', 'history_museum', 'science_museum')",
    library: "category = 'library'",
    mosque: "category = 'mosque'",
    temple: "category = 'temple'",
    church: "category IN ('church', 'church_cathedral', 'catholic_church', 'baptist_church')",
    synagogue: "category = 'synagogue'",
    park: "category IN ('park', 'national_park', 'nature_reserve')",
    zoo: "category = 'zoo'",
    embassy: "category = 'embassy'",
    train_station: "category = 'train_station'",
    bus_station: "category = 'bus_station'",
    port: "category IN ('port', 'marina')",
    marina: "category = 'marina'",
    stadium:
      "category IN ('stadium', 'stadium_arena', 'sports_complex', 'sports_and_recreation_venue')",
    cafe: "category IN ('cafe', 'coffee_shop')",
    coffee_shop: "category IN ('cafe', 'coffee_shop')",
    tea_room: "category IN ('tea_room', 'bubble_tea')",
    botanical_garden: "category = 'botanical_garden'",
    restaurant: "category IN ('restaurant', 'fast_food_restaurant')",
    hotel: "category IN ('hotel', 'accommodation', 'bed_and_breakfast', 'hostel', 'resort')",
    bar: "category IN ('bar', 'pub', 'cocktail_bar', 'wine_bar', 'beer_bar', 'sports_bar')",
    bakery: "category = 'bakery'",
    pharmacy: "category IN ('pharmacy', 'drugstore')",
    bank: "category IN ('bank', 'bank_credit_union', 'credit_union')",
    supermarket: "category IN ('supermarket', 'grocery_store')",
    gas_station: "category IN ('gas_station', 'petrol_station')",
    gym: "category IN ('gym', 'yoga_studio', 'pilates_studio', 'fitness_center')",
    cinema: "category IN ('cinema', 'movie_theater')",
    post_office: "category = 'post_office'",
    fire_department: "category IN ('fire_department', 'fire_station')",
    police_department: "category IN ('police_department', 'police_station')",
    beach: "category = 'beach'",
    castle: "category IN ('castle', 'palace')",
    lighthouse: "category = 'lighthouse'",
    monument: "category = 'monument'",
    landmark: "category IN ('landmark_and_historical_building', 'monument', 'castle', 'palace')",
    dentist: "category IN ('dentist', 'general_dentistry')",
    doctor: "category IN ('doctor', 'family_practice', 'internal_medicine')",
    veterinarian: "category IN ('veterinarian', 'veterinary_care')",
    golf_course: "category = 'golf_course'",
    swimming_pool: "category = 'swimming_pool'",
    bowling_alley: "category = 'bowling_alley'",
    ice_skating_rink: "category IN ('ice_skating_rink', 'ice_rink')",
    amusement_park: "category IN ('amusement_park', 'theme_park')",
    water_park: "category = 'water_park'",
    aquarium: "category = 'aquarium'",
    ski_resort: "category = 'ski_resort'",
    winery: "category IN ('winery', 'vineyard')",
    brewery: "category = 'brewery'",
    bookstore: "category IN ('bookstore', 'book_store')",
    shopping_mall: "category = 'shopping_mall'",
    campground: "category IN ('campground', 'camp_site')",
    nightclub: "category IN ('nightclub', 'bar')",
  }
  return categoryMap[category] || `category ILIKE '%${category.replace(/'/g, "''")}%'`
}

/**
 * Map a user-facing category to one or more Overture category parquet file names.
 * Each file lives at: R2/data/categories/{name}.parquet
 * @param {string} category
 * @returns {string[]} Array of parquet file base names
 */
export function getCategoryParquetFiles(category) {
  const multiFileMap = {
    hospital: ['hospital', 'medical_center'],
    museum: ['museum', 'art_gallery', 'art_museum', 'history_museum', 'science_museum'],
    park: ['national_park', 'nature_reserve', 'park'],
    port: ['port', 'marina'],
    stadium: ['stadium_arena', 'sports_and_recreation_venue'],
    cafe: ['cafe', 'coffee_shop'],
    coffee_shop: ['cafe', 'coffee_shop'],
    tea_room: ['tea_room', 'bubble_tea'],
    church: [
      'church_cathedral',
      'catholic_church',
      'baptist_church',
      'anglican_church',
      'pentecostal_church',
      'episcopal_church',
      'evangelical_church',
    ],
    restaurant: ['restaurant', 'fast_food_restaurant'],
    hotel: ['hotel', 'accommodation', 'bed_and_breakfast', 'hostel', 'resort'],
    bank: ['bank_credit_union', 'banks', 'credit_union'],
    pharmacy: ['pharmacy', 'drugstore'],
    supermarket: ['supermarket', 'grocery_store'],
    gym: ['gym', 'yoga_studio', 'pilates_studio'],
    bar: ['bar', 'pub', 'cocktail_bar', 'wine_bar', 'beer_bar', 'sports_bar'],
    dentist: ['dentist', 'general_dentistry', 'orthodontist', 'pediatric_dentist'],
    doctor: ['doctor', 'family_practice', 'internal_medicine'],
    school: [
      'school',
      'elementary_school',
      'middle_school',
      'high_school',
      'private_school',
      'public_school',
    ],
    university: ['college_university'],
    landmark: ['landmark_and_historical_building', 'monument', 'castle', 'palace'],
  }
  if (multiFileMap[category]) return multiFileMap[category]
  // Single category → single file
  return [category]
}

/**
 * Format a POI category for display labels.
 * @param {string} category
 * @returns {string} Human-readable plural form
 */
export function formatPOICategory(category) {
  const names = {
    hospital: 'Hospitals',
    school: 'Schools',
    university: 'Universities',
    airport: 'Airports',
    museum: 'Museums',
    library: 'Libraries',
    mosque: 'Mosques',
    temple: 'Temples',
    church: 'Churches',
    park: 'Parks',
    zoo: 'Zoos',
    embassy: 'Embassies',
    train_station: 'Train Stations',
    bus_station: 'Bus Stations',
    port: 'Ports',
    stadium: 'Stadiums',
    cafe: 'Cafés & Coffee Shops',
    coffee_shop: 'Cafés & Coffee Shops',
    tea_room: 'Tea Rooms',
    botanical_garden: 'Botanical Gardens',
    restaurant: 'Restaurants',
    hotel: 'Hotels',
    bar: 'Bars & Pubs',
    bakery: 'Bakeries',
    pharmacy: 'Pharmacies',
    bank: 'Banks',
    supermarket: 'Supermarkets',
    gas_station: 'Gas Stations',
    gym: 'Gyms',
    cinema: 'Cinemas',
    post_office: 'Post Offices',
    fire_department: 'Fire Departments',
    police_department: 'Police Departments',
    beach: 'Beaches',
    campground: 'Campgrounds',
    landmark: 'Landmarks',
    castle: 'Castles',
    lighthouse: 'Lighthouses',
    monument: 'Monuments',
    dentist: 'Dentists',
    doctor: 'Doctors',
    veterinarian: 'Veterinarians',
    golf_course: 'Golf Courses',
    swimming_pool: 'Swimming Pools',
    bowling_alley: 'Bowling Alleys',
    ice_skating_rink: 'Ice Skating Rinks',
    amusement_park: 'Amusement Parks',
    water_park: 'Water Parks',
    aquarium: 'Aquariums',
    ski_resort: 'Ski Resorts',
  }
  return names[category] || category
}

// ══════════════════════════════════════════════════════════════════════
// Overture Maps utilities
// ══════════════════════════════════════════════════════════════════════

/**
 * Get legend configuration for an Overture theme.
 * @param {string} theme — 'water', 'transportation', 'buildings', 'places'
 * @returns {object|null} Legend configuration
 */
export function getOvertureLegend(theme) {
  const legends = {
    water: {
      visible: true,
      title: 'Water Features (Overture Maps)',
      type: 'categorical',
      stops: [
        { color: [26, 58, 92, 255], label: 'Ocean' },
        { color: [37, 99, 168, 255], label: 'Lake' },
        { color: [79, 195, 247, 255], label: 'River / Stream' },
      ],
    },
    land: {
      visible: true,
      title: 'Land (Overture Maps)',
      type: 'categorical',
      stops: [
        { color: [45, 74, 45, 255], label: 'Land' },
        { color: [58, 90, 64, 255], label: 'Island' },
        { color: [232, 244, 248, 255], label: 'Glacier' },
        { color: [232, 213, 163, 255], label: 'Sand' },
        { color: [139, 134, 128, 255], label: 'Rock' },
      ],
    },
    land_use: {
      visible: true,
      title: 'Land Use (Overture Maps)',
      type: 'categorical',
      stops: [
        { color: [34, 197, 94, 255], label: 'Park' },
        { color: [21, 128, 61, 255], label: 'Forest' },
        { color: [168, 85, 247, 255], label: 'Residential' },
        { color: [245, 158, 11, 255], label: 'Commercial' },
        { color: [239, 68, 68, 255], label: 'Industrial' },
        { color: [59, 130, 246, 255], label: 'Education' },
        { color: [163, 230, 53, 255], label: 'Farmland' },
        { color: [6, 182, 212, 255], label: 'Aquaculture' },
      ],
    },
    transportation: {
      visible: true,
      title: 'Transportation (Overture Maps)',
      type: 'categorical',
      stops: [
        { color: [255, 140, 0, 255], label: 'Motorway' },
        { color: [255, 170, 51, 255], label: 'Primary Road' },
        { color: [255, 204, 102, 255], label: 'Secondary Road' },
        { color: [224, 224, 224, 255], label: 'Railway' },
      ],
    },
    buildings: {
      visible: true,
      title: '3D Buildings (Overture Maps)',
      type: 'categorical',
      stops: [
        { color: [111, 168, 220, 255], label: 'Residential' },
        { color: [232, 168, 56, 255], label: 'Commercial' },
        { color: [204, 102, 119, 255], label: 'Industrial' },
        { color: [74, 144, 217, 255], label: 'Education' },
        { color: [224, 85, 85, 255], label: 'Medical' },
        { color: [155, 136, 196, 255], label: 'Civic' },
        { color: [180, 180, 200, 255], label: 'Other' },
      ],
    },
    places: {
      visible: true,
      title: 'Places (Overture Maps)',
      type: 'categorical',
      stops: [
        { color: [66, 133, 244, 255], label: 'Education' },
        { color: [234, 67, 53, 255], label: 'Healthcare' },
        { color: [156, 39, 176, 255], label: 'Airport' },
        { color: [0, 150, 136, 255], label: 'Culture' },
        { color: [76, 175, 80, 255], label: 'Nature' },
        { color: [255, 87, 34, 255], label: 'Restaurant' },
        { color: [158, 158, 158, 255], label: 'Other' },
      ],
    },
  }
  return legends[theme] || null
}

// ══════════════════════════════════════════════════════════════════════
// Voice helpers
// ══════════════════════════════════════════════════════════════════════

/**
 * Speak a voice response from an intent, using template interpolation.
 * Handles both voiceResponse.template and intent.speech patterns.
 * Sets ctx.spokeDuringRoute = true to prevent double-speaking.
 *
 * @param {object} intent — parsed intent
 * @param {object} ctx — pipeline context
 */
export async function speakResponse(intent, ctx) {
  const speech = ctx.services.speech
  if (!speech) return

  if (intent.voiceResponse?.shouldSpeak && intent.voiceResponse?.template) {
    const template = interpolateTemplate(intent.voiceResponse.template, ctx)
    await speech.speak(template)
    ctx.spokeDuringRoute = true
  } else if (intent.speech) {
    await speech.speak(intent.speech)
    ctx.spokeDuringRoute = true
  }
}

// ══════════════════════════════════════════════════════════════════════
// Correlation statistics (bivariate choropleth companion)
// Playbook ref: SPATIAL-ANALYSIS-PLAYBOOK.md §D4
// ══════════════════════════════════════════════════════════════════════

/**
 * Pearson correlation between two numeric feature attributes.
 * Pure function — pairs with any nulls/NaN are dropped.
 *
 * @param {Array} features — GeoJSON features
 * @param {string} attr1
 * @param {string} attr2
 * @returns {{ r: number, r2: number, n: number } | null} null when < 3 valid pairs
 */
export function computeCorrelation(features, attr1, attr2) {
  const xs = []
  const ys = []
  for (const f of features || []) {
    const rawX = f?.properties?.[attr1]
    const rawY = f?.properties?.[attr2]
    // Explicit null/empty check: Number(null) === 0 would silently turn
    // missing data into zeros and corrupt the statistic.
    if (rawX == null || rawY == null || rawX === '' || rawY === '') continue
    const x = Number(rawX)
    const y = Number(rawY)
    if (Number.isFinite(x) && Number.isFinite(y)) {
      xs.push(x)
      ys.push(y)
    }
  }
  const n = xs.length
  if (n < 3) return null

  const mx = xs.reduce((a, b) => a + b, 0) / n
  const my = ys.reduce((a, b) => a + b, 0) / n
  let sxy = 0
  let sxx = 0
  let syy = 0
  for (let i = 0; i < n; i++) {
    const dx = xs[i] - mx
    const dy = ys[i] - my
    sxy += dx * dy
    sxx += dx * dx
    syy += dy * dy
  }
  if (sxx === 0 || syy === 0) return null // constant variable → undefined r

  const r = sxy / Math.sqrt(sxx * syy)
  // OLS simple regression (Grekousis ch. 6): slope/intercept from the same
  // sums — no extra pass. Kept at full precision; rounding happens at
  // narration time so step math stays exact.
  const slope = sxy / sxx
  return {
    r: Math.round(r * 1000) / 1000,
    r2: Math.round(r * r * 1000) / 1000,
    n,
    slope,
    intercept: my - slope * mx,
    xMin: Math.min(...xs),
    xMax: Math.max(...xs),
  }
}

/** Snap a range to a "nice" step (1/2/5 × 10^k ≈ range/5) for narration. */
function niceStep(range) {
  if (!Number.isFinite(range) || range <= 0) return null
  const raw = range / 5
  const k = Math.pow(10, Math.floor(Math.log10(raw)))
  const m = raw / k
  const snapped = m < 1.5 ? 1 : m < 3.5 ? 2 : m < 7.5 ? 5 : 10
  return snapped * k
}

/** Compact human formatting for narrated quantities. */
function fmtQty(v) {
  const abs = Math.abs(v)
  if (abs >= 1000) return Math.round(v).toLocaleString('en-US')
  if (abs >= 100) return String(Math.round(v))
  if (abs >= 1) {
    const r = Math.round(v * 10) / 10
    return Number.isInteger(r) ? String(r) : r.toFixed(1)
  }
  return String(Number(v.toPrecision(2)))
}

/**
 * Classroom narration of the regression slope (Grekousis ch. 6, simple OLS).
 * Only speaks when the relationship is at least moderate (|r| ≥ 0.4) —
 * narrating a slope through a cloud of noise would teach the wrong lesson.
 * Example: "On average, each increase of 10,000 in GDP per capita is
 * associated with about 2.1 higher life expectancy."
 */
export function describeRegression(corr, label1, label2) {
  if (!corr || !Number.isFinite(corr.slope)) return ''
  if (Math.abs(corr.r) < 0.4) return ''
  const step = niceStep(corr.xMax - corr.xMin)
  if (!step) return ''
  const delta = corr.slope * step
  if (!Number.isFinite(delta) || delta === 0) return ''
  const direction = delta > 0 ? 'higher' : 'lower'
  return (
    ` On average, each increase of ${fmtQty(step)} in ${label1}` +
    ` is associated with about ${fmtQty(Math.abs(delta))} ${direction} ${label2}.`
  )
}

/**
 * Classroom-honest narration of a correlation result.
 * Qualitative bands on |r|: <0.2 negligible, <0.4 weak, <0.6 moderate,
 * <0.8 strong, else very strong. Always notes correlation ≠ causation.
 */
export function describeCorrelation(corr, label1, label2) {
  if (!corr) return ''
  const abs = Math.abs(corr.r)
  const strength =
    abs < 0.2
      ? 'negligible'
      : abs < 0.4
        ? 'weak'
        : abs < 0.6
          ? 'moderate'
          : abs < 0.8
            ? 'strong'
            : 'very strong'
  const direction = corr.r >= 0 ? 'positive' : 'negative'
  if (abs < 0.2) {
    return ` Across ${corr.n} countries, there is almost no linear relationship between ${label1} and ${label2} (r = ${corr.r}).`
  }
  return (
    ` Across ${corr.n} countries, there is a ${strength} ${direction} relationship between ${label1} and ${label2}` +
    ` (r = ${corr.r}, R² = ${corr.r2}). Remember: correlation does not prove causation.`
  )
}
